import { SITE, DEFAULT_DESCRIPTION, absoluteUrl, buildTitle } from '../constants/seo';

/** Trims a string to a meta-description-friendly length on a word boundary. */
export const toMetaDescription = (text, max = 160) => {
  if (!text) return '';
  const flat = String(text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (flat.length <= max) return flat;
  return `${flat.slice(0, flat.lastIndexOf(' ', max - 1)).trim()}…`;
};

const toIsoDate = (value) => {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

/**
 * Site-wide entity graph, rendered once on the homepage. Everything else links
 * back to these @id values instead of redeclaring the organisation.
 */
export const buildOrganizationSchema = () => [
  {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'MedicalBusiness'],
    '@id': `${SITE.url}/#organization`,
    name: SITE.legalName,
    alternateName: SITE.name,
    url: SITE.url,
    logo: { '@type': 'ImageObject', url: SITE.ogImage },
    description: DEFAULT_DESCRIPTION,
    medicalSpecialty: 'Hypnotherapy',
    availableService: {
      '@type': 'MedicalTherapy',
      name: 'Interpersonal Hypnotherapy',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE.url}/#website`,
    url: SITE.url,
    name: SITE.legalName,
    publisher: { '@id': `${SITE.url}/#organization` },
    inLanguage: 'en',
  },
];

/**
 * Maps a blog post record onto <Seo /> props: article metadata plus the
 * Article + BreadcrumbList JSON-LD that Google reads for news and blog results.
 */
export const buildBlogPostSeo = (post) => {
  if (!post) return {};

  const path = `/blog/${post.slug}`;
  const url = absoluteUrl(path);
  // Tags are authored as hashtags in the dashboard; schema.org keywords and
  // article:tag both expect plain terms.
  const tags = (Array.isArray(post.tags) ? post.tags : [])
    .map((tag) => String(tag).replace(/^#+/, '').trim())
    .filter(Boolean);
  const description = toMetaDescription(post.excerpt || post.body) || DEFAULT_DESCRIPTION;
  const published = toIsoDate(post.date);
  const image = post.coverImage || SITE.ogImage;

  // Practitioner posts are bylined to the person; house posts to the clinic.
  const author =
    post.authorType === 'practitioner' && post.authorName
      ? { '@type': 'Person', name: post.authorName }
      : { '@type': 'Organization', name: SITE.legalName, url: SITE.url };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description,
    image: [image],
    author,
    publisher: {
      '@type': 'Organization',
      name: SITE.legalName,
      logo: { '@type': 'ImageObject', url: SITE.ogImage },
    },
    datePublished: published,
    dateModified: published,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    ...(post.category ? { articleSection: post.category } : {}),
    ...(tags.length ? { keywords: tags.join(', ') } : {}),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE.url },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: absoluteUrl('/blog') },
      { '@type': 'ListItem', position: 3, name: post.title, item: url },
    ],
  };

  return {
    title: post.title,
    description,
    path,
    image,
    type: 'article',
    article: {
      publishedTime: published,
      modifiedTime: published,
      author: post.authorName,
      section: post.category,
      tags,
    },
    schema: [articleSchema, breadcrumbSchema],
  };
};

/**
 * Maps a practitioner record onto <Seo /> props, including Person schema so the
 * profile can surface as an entity rather than a generic page.
 */
export const buildPractitionerSeo = (practitioner, path) => {
  if (!practitioner?.fullName) return {};

  const url = absoluteUrl(path);
  // The GHL "title" field is free text and some practitioners use it to list
  // every business and credential they hold. Anything that long is not a job
  // title and would blow past what a search result can show, so fall back.
  const DEFAULT_ROLE = 'Interpersonal Hypnotherapy Practitioner';
  const rawRole = (practitioner.title || '').trim();
  const role = rawRole && rawRole.length <= 40 ? rawRole : DEFAULT_ROLE;
  const description =
    toMetaDescription(practitioner.fullBio) ||
    `${practitioner.fullName} is a certified practitioner at ${SITE.legalName}. Book a free 30-minute consultation.`;

  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: practitioner.fullName,
    jobTitle: role,
    description,
    url,
    ...(practitioner.avatar ? { image: practitioner.avatar } : {}),
    ...(practitioner.languages ? { knowsLanguage: practitioner.languages } : {}),
    worksFor: { '@type': 'Organization', name: SITE.legalName, url: SITE.url },
  };

  return {
    title: buildTitle(`${practitioner.fullName}, ${role}`),
    description,
    path,
    image: practitioner.avatar,
    type: 'profile',
    schema: personSchema,
  };
};
